import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import { Calendar, CheckCircle, XCircle, Clock, AlertCircle, Plus, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

interface AttendanceRecord {
  id: number;
  child_name: string;
  session_name: string;
  date: string;
  status: 'present' | 'absent' | 'late' | 'excused';
  notes: string;
}

interface AttendanceRequest {
  id: number;
  child_name: string;
  session_name: string;
  request_type: string;
  original_date: string;
  requested_date: string | null;
  reason: string;
  reason_details: string;
  status: 'pending' | 'approved' | 'denied' | 'completed';
  admin_response: string;
  created_at: string;
}

interface MakeupSession {
  id: number;
  child_name: string;
  program_name: string;
  session_name: string;
  scheduled_date: string;
  scheduled_time: string;
  status: 'scheduled' | 'completed' | 'cancelled';
  notes: string;
}

const STATUS_ICONS = {
  present: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100' },
  absent: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100' },
  late: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-100' },
  excused: { icon: AlertCircle, color: 'text-blue-600', bg: 'bg-blue-100' },
};

const REQUEST_STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  denied: 'bg-red-100 text-red-800',
  completed: 'bg-blue-100 text-blue-800',
};

const MyAttendance: React.FC = () => {
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedChild, setSelectedChild] = useState('');
  const queryClient = useQueryClient();

  const { data: attendance, isLoading: loadingAttendance } = useQuery('my-attendance', async () => {
    const res = await api.get('/registrations/attendance/my_children/');
    // Ensure we always return an array
    const data = res.data;
    if (Array.isArray(data)) return data;
    if (data?.results && Array.isArray(data.results)) return data.results;
    return [];
  });

  const { data: requests, isLoading: loadingRequests } = useQuery('my-attendance-requests', async () => {
    const res = await api.get('/registrations/attendance-requests/my_requests/');
    const data = res.data;
    if (Array.isArray(data)) return data;
    if (data?.results && Array.isArray(data.results)) return data.results;
    return [];
  });

  const { data: registrations } = useQuery('my-active-registrations', async () => {
    const res = await api.get('/registrations/registrations/my_registrations/');
    const data = res.data;
    const arr = Array.isArray(data) ? data : (data?.results || []);
    return arr.filter((r: any) => r.status === 'confirmed');
  });

  // Fetch makeup sessions for parent
  const { data: makeupSessions } = useQuery('my-makeup-sessions', async () => {
    const res = await api.get('/registrations/makeup-sessions/');
    const data = res.data;
    if (Array.isArray(data)) return data;
    if (data?.results && Array.isArray(data.results)) return data.results;
    return [];
  });

  const scheduledMakeups = Array.isArray(makeupSessions) 
    ? makeupSessions.filter((m: MakeupSession) => m.status === 'scheduled') 
    : [];

  const createRequestMutation = useMutation(
    async (data: any) => {
      const res = await api.post('/registrations/attendance-requests/', data);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('my-attendance-requests');
        setShowRequestModal(false);
        toast.success('Request submitted successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to submit request');
      }
    }
  );

  const handleSubmitRequest = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    createRequestMutation.mutate({
      registration: parseInt(formData.get('registration') as string),
      request_type: formData.get('request_type'),
      original_date: formData.get('original_date'),
      requested_date: formData.get('requested_date') || null,
      reason: formData.get('reason'),
      reason_details: formData.get('reason_details'),
    });
  };

  if (loadingAttendance || loadingRequests) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="animate-spin text-primary-500" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Attendance & Requests</h1>
        <p className="text-gray-600 mt-1">View attendance records and submit requests</p>
      </div>

      {/* Scheduled Makeup Sessions - Orange Banner */}
      {scheduledMakeups.length > 0 && (
        <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="text-orange-600" size={28} />
            <div>
              <h2 className="text-xl font-bold text-orange-800">Upcoming Makeup Sessions</h2>
              <p className="text-sm text-orange-600">These sessions have been scheduled by admin</p>
            </div>
          </div>
          <div className="space-y-3">
            {scheduledMakeups.map((makeup: MakeupSession) => (
              <div key={makeup.id} className="bg-white border border-orange-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{makeup.child_name}</h3>
                    <p className="text-sm text-gray-600">{makeup.program_name}</p>
                    {makeup.session_name && (
                      <p className="text-sm text-gray-500">Session: {makeup.session_name}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-orange-700">
                      {new Date(makeup.scheduled_date).toLocaleDateString('en-US', { 
                        weekday: 'short', month: 'short', day: 'numeric' 
                      })}
                    </p>
                    {makeup.scheduled_time && (
                      <p className="text-sm text-orange-600">{makeup.scheduled_time}</p>
                    )}
                  </div>
                </div>
                {makeup.notes && (
                  <p className="mt-2 text-sm text-gray-500 bg-orange-50 rounded p-2">{makeup.notes}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attendance Requests Section */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">My Requests</h2>
          <button
            onClick={() => setShowRequestModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            <Plus size={20} />
            <span>New Request</span>
          </button>
        </div>

        {requests?.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No attendance requests yet</p>
        ) : (
          <div className="space-y-4">
            {requests?.map((request: AttendanceRequest) => (
              <motion.div
                key={request.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-semibold">{request.child_name}</h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${REQUEST_STATUS_COLORS[request.status]}`}>
                        {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">
                      <strong>Type:</strong> {request.request_type.replace('_', ' ')}
                    </p>
                    <p className="text-sm text-gray-600 mb-1">
                      <strong>Session:</strong> {request.session_name}
                    </p>
                    <p className="text-sm text-gray-600 mb-1">
                      <strong>Original Date:</strong> {new Date(request.original_date).toLocaleDateString()}
                    </p>
                    {request.requested_date && (
                      <p className="text-sm text-gray-600 mb-1">
                        <strong>Requested Date:</strong> {new Date(request.requested_date).toLocaleDateString()}
                      </p>
                    )}
                    <p className="text-sm text-gray-600 mb-1">
                      <strong>Reason:</strong> {request.reason.replace('_', ' ')}
                    </p>
                    {request.reason_details && (
                      <p className="text-sm text-gray-600 mb-1">
                        <strong>Details:</strong> {request.reason_details}
                      </p>
                    )}
                    {request.admin_response && (
                      <div className="mt-2 p-3 bg-gray-50 rounded">
                        <p className="text-sm font-medium text-gray-700">Admin Response:</p>
                        <p className="text-sm text-gray-600">{request.admin_response}</p>
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(request.created_at).toLocaleDateString()}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Attendance Records Section - Grouped by Child */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Attendance Summary</h2>
        
        {!Array.isArray(attendance) || attendance.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No attendance records yet</p>
        ) : (
          <div className="space-y-6">
            {attendance.map((child: any) => (
              <div key={child.child_id} className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{child.child_name}</h3>
                
                {child.programs?.length === 0 ? (
                  <p className="text-sm text-gray-500">No program enrollments</p>
                ) : (
                  <div className="space-y-4">
                    {child.programs?.map((program: any, idx: number) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-800">{program.program_name}</h4>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            program.attendance_rate >= 80 ? 'bg-green-100 text-green-800' :
                            program.attendance_rate >= 60 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {program.attendance_rate}% Attendance
                          </span>
                        </div>
                        
                        {/* Session Name */}
                        {program.session_name && (
                          <p className="text-sm text-gray-500 mb-2">Session: {program.session_name}</p>
                        )}

                        {/* Progress Bar */}
                        {(() => {
                          const totalSessions = program.total_program_sessions || 4;
                          const sessionsHeld = program.sessions_held || program.sessions_completed || 0;
                          const progressPercent = program.progress_percent || Math.min(100, Math.round((sessionsHeld / totalSessions) * 100));
                          const isCompleted = program.is_completed;
                          const remaining = totalSessions - sessionsHeld;
                          return (
                            <div className="mb-4">
                              <div className="flex justify-between text-sm text-gray-600 mb-1">
                                <span>{sessionsHeld} of {totalSessions} classes held</span>
                                {isCompleted ? (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-medium">✓ Program Complete!</span>
                                ) : (
                                  <span>{remaining} class{remaining !== 1 ? 'es' : ''} remaining</span>
                                )}
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-3">
                                <div 
                                  className={`h-3 rounded-full transition-all ${
                                    isCompleted ? 'bg-green-500' :
                                    progressPercent >= 75 ? 'bg-green-500' :
                                    progressPercent >= 50 ? 'bg-blue-500' :
                                    progressPercent >= 25 ? 'bg-yellow-500' :
                                    'bg-gray-400'
                                  }`}
                                  style={{ width: `${progressPercent}%` }}
                                />
                              </div>
                              <p className="text-xs text-gray-500 mt-1 text-right">{progressPercent}% complete</p>
                            </div>
                          );
                        })()}

                        <div className="grid grid-cols-4 gap-3 mb-3">
                          <div className="text-center p-2 bg-green-50 rounded">
                            <p className="text-2xl font-bold text-green-600">{program.present}</p>
                            <p className="text-xs text-green-700">Present</p>
                          </div>
                          <div className="text-center p-2 bg-red-50 rounded">
                            <p className="text-2xl font-bold text-red-600">{program.absent}</p>
                            <p className="text-xs text-red-700">Absent</p>
                          </div>
                          <div className="text-center p-2 bg-yellow-50 rounded">
                            <p className="text-2xl font-bold text-yellow-600">{program.late}</p>
                            <p className="text-xs text-yellow-700">Late</p>
                          </div>
                          <div className="text-center p-2 bg-blue-50 rounded">
                            <p className="text-2xl font-bold text-blue-600">{program.excused}</p>
                            <p className="text-xs text-blue-700">Excused</p>
                          </div>
                        </div>
                        
                        {program.recent_records?.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-gray-700 mb-2">Recent Sessions:</p>
                            <div className="space-y-1">
                              {program.recent_records.map((record: any, rIdx: number) => {
                                const statusInfo = STATUS_ICONS[record.status as keyof typeof STATUS_ICONS];
                                const Icon = statusInfo?.icon || AlertCircle;
                                return (
                                  <div key={rIdx} className="flex items-center justify-between text-sm py-1">
                                    <span className="text-gray-600">{record.date}</span>
                                    <div className={`flex items-center space-x-1 ${statusInfo?.color || 'text-gray-600'}`}>
                                      <Icon size={14} />
                                      <span className="capitalize">{record.status}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Request Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">Submit Attendance Request</h2>
            
            <form onSubmit={handleSubmitRequest} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Child/Session</label>
                <select
                  name="registration"
                  required
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select a registration...</option>
                  {registrations?.map((reg: any) => (
                    <option key={reg.id} value={reg.id}>
                      {reg.child_name} - {reg.session_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Request Type</label>
                <select
                  name="request_type"
                  required
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="absence">Report Absence</option>
                  <option value="reschedule">Request Reschedule</option>
                  <option value="cancel_class">Cancel Single Class</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Original Date</label>
                <input
                  type="date"
                  name="original_date"
                  required
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Requested Makeup Date (optional)</label>
                <input
                  type="date"
                  name="requested_date"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <select
                  name="reason"
                  required
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="sick">Child is Sick</option>
                  <option value="family_emergency">Family Emergency</option>
                  <option value="vacation">Vacation/Travel</option>
                  <option value="conflict">Schedule Conflict</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Details</label>
                <textarea
                  name="reason_details"
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Please provide any additional details..."
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowRequestModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createRequestMutation.isLoading}
                  className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
                >
                  {createRequestMutation.isLoading ? (
                    <Loader2 className="animate-spin mx-auto" size={20} />
                  ) : (
                    'Submit Request'
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default MyAttendance;
